import { useState } from 'preact/hooks';
import { SiteLayout } from '../../components/SiteLayout';
import { ErrorBox, Notice } from '../../components/ui';
import { useHumanCheck } from '../../components/Turnstile';
import { usePageMeta } from '../../lib/meta';
import { api, errorMessage } from '../../lib/api';

/** Matches the server's repeat-infringer threshold (server/src/routes/reports.ts REPEAT_INFRINGER_STRIKES). */
const STRIKES = 3;
export const GOOD_FAITH = 'I have a good-faith belief that the use of the material is not authorized by the copyright owner, its agent, or the law.';
export const ACCURATE = 'The information in this notice is accurate, and under penalty of perjury, I am the owner or authorized to act on behalf of the owner.';

export function Copyright() {
  usePageMeta('Copyright & takedown', 'How to report copyright infringement in Ayonix Studio cloud downloads, what a notice must contain, counter-notices and the repeat-infringer policy.');
  return (
    <SiteLayout>
      <article class="container prose">
        <header class="page-hero"><h1>Copyright &amp; takedown</h1><p>How to report material stored by Ayonix Studio, and what happens next.</p></header>

        <h2>What we store</h2>
        <p>The only media we store are <strong>cloud downloads</strong>: copies a signed-in user fetched from an authorized provider, kept for a limited time. Browser downloads go straight from the source to the user’s device and never pass through our servers.</p>

        <h2>Content we don’t host</h2>
        <ul>
          <li><strong>YouTube:</strong> videos are shown through YouTube’s own player and are never stored by us. Report them to YouTube with its <a href="https://support.google.com/youtube/answer/2807622" rel="noopener" target="_blank">copyright removal request</a>.</li>
          <li><strong>Wikimedia Commons:</strong> the original files are hosted by Wikimedia. Report a cloud copy to us, and report the original to <a href="https://commons.wikimedia.org/wiki/Commons:Contact_us" rel="noopener" target="_blank">Wikimedia Commons</a> too.</li>
        </ul>

        <h2>How to send a notice</h2>
        <p>Use the form below or email <a href="mailto:support@ayonix.com">support@ayonix.com</a>. A notice must include:</p>
        <ul>
          <li>your name and an email address where we can reach you;</li>
          <li>the copyrighted work you believe is infringed;</li>
          <li>the exact URL of the material — for a cloud file, the download link (it contains <code>/files/</code>);</li>
          <li>a statement that you have a good-faith belief the use isn’t authorized by the owner, its agent or the law;</li>
          <li>a statement that the notice is accurate and, under penalty of perjury, that you are the owner or authorized to act for the owner;</li>
          <li>your physical or electronic signature (typing your full name is enough).</li>
        </ul>

        <h2>What happens next</h2>
        <ul>
          <li>We review notices promptly and remove the reported cloud file.</li>
          <li>The account holder is told by email. If they believe the removal was a mistake, they can send a counter-notice to <a href="mailto:support@ayonix.com">support@ayonix.com</a>.</li>
          <li>Repeat infringers lose cloud downloads: an account that receives {STRIKES} valid notices can no longer use them.</li>
          <li>Knowingly false notices or counter-notices can make the sender liable for damages.</li>
        </ul>

        <ReportForm />
      </article>
    </SiteLayout>
  );
}

const EMPTY = { name: '', email: '', work: '', url: '', details: '', signature: '', goodFaith: false, accurate: false };

function validate(f: typeof EMPTY): string | null {
  if (f.name.trim().length < 2) return 'Enter your full name.';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) return 'Enter a valid email address so we can contact you.';
  if (f.work.trim().length < 10) return 'Describe the copyrighted work (at least 10 characters).';
  try { const u = new URL(f.url.trim()); if (u.protocol !== 'https:' && u.protocol !== 'http:') throw new Error(); }
  catch { return 'Enter the full URL of the material you are reporting, starting with https://.'; }
  if (!f.goodFaith || !f.accurate) return 'Confirm both statements to send the notice.';
  if (f.signature.trim().length < 2) return 'Type your full name as your signature.';
  return null;
}

function ReportForm() {
  const [f, setF] = useState(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ id: string; message: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const human = useHumanCheck('report');
  const set = (k: keyof typeof EMPTY) => (e: { currentTarget: HTMLInputElement | HTMLTextAreaElement }) =>
    setF((p) => ({ ...p, [k]: e.currentTarget instanceof HTMLInputElement && e.currentTarget.type === 'checkbox' ? e.currentTarget.checked : e.currentTarget.value }));

  const submit = async (e: Event) => {
    e.preventDefault();
    setError(null); setDone(null);
    const problem = validate(f);
    if (problem) { setError(problem); return; }
    if (human.required && !human.token) { setError(human.pendingMessage); return; }
    setBusy(true);
    try {
      const r = await api<{ id: string; status: string; message: string }>('/v1/reports', {
        method: 'POST',
        body: {
          name: f.name.trim(), email: f.email.trim(), work: f.work.trim(), url: f.url.trim(), details: f.details.trim() || undefined,
          goodFaith: f.goodFaith, accurate: f.accurate, signature: f.signature.trim(), client: 'web', turnstileToken: human.token ?? undefined,
        },
      });
      setDone({ id: r.id, message: r.message });
      setF(EMPTY);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      human.reset();
      setBusy(false);
    }
  };

  return (
    <section class="panel stack report-form" aria-labelledby="report-h" style="margin-top:32px">
      <h2 id="report-h" style="margin:0">Send a copyright notice</h2>
      {done && <div data-testid="report-sent"><Notice tone="success"><p>{done.message} Reference: <code>{done.id}</code>.</p></Notice></div>}
      <form class="stack" onSubmit={submit} noValidate>
        <div class="grid-2">
          <div class="field"><label for="r-name">Your name</label><input id="r-name" class="input" autoComplete="name" required value={f.name} onInput={set('name')} /></div>
          <div class="field"><label for="r-email">Email</label><input id="r-email" class="input" type="email" autoComplete="email" required value={f.email} onInput={set('email')} /></div>
        </div>
        <div class="field">
          <label for="r-work">Copyrighted work</label>
          <textarea id="r-work" class="input" required rows={3} aria-describedby="r-work-hint" value={f.work} onInput={set('work')} />
          <span id="r-work-hint" class="hint">For example the title, and where the original is published.</span>
        </div>
        <div class="field">
          <label for="r-url">URL of the material</label>
          <input id="r-url" class="input" type="url" inputMode="url" required spellcheck={false} aria-describedby="r-url-hint" value={f.url} onInput={set('url')} />
          <span id="r-url-hint" class="hint">For a cloud file, paste its download link.</span>
        </div>
        <div class="field"><label for="r-details">Additional details (optional)</label><textarea id="r-details" class="input" rows={3} value={f.details} onInput={set('details')} /></div>
        <label class="check-row"><input type="checkbox" required checked={f.goodFaith} onChange={set('goodFaith')} /><span>{GOOD_FAITH}</span></label>
        <label class="check-row"><input type="checkbox" required checked={f.accurate} onChange={set('accurate')} /><span>{ACCURATE}</span></label>
        <div class="field">
          <label for="r-sign">Signature</label>
          <input id="r-sign" class="input" required autoComplete="name" aria-describedby="r-sign-hint" value={f.signature} onInput={set('signature')} />
          <span id="r-sign-hint" class="hint">Type your full name.</span>
        </div>
        {human.box}
        {error && <ErrorBox message={error} />}
        <button class="btn primary" type="submit" disabled={busy} style="align-self:flex-start">{busy ? 'Sending…' : 'Send notice'}</button>
      </form>
    </section>
  );
}
